import React, { lazy, useCallback, useEffect } from "react";

import { useAdmin } from "providers/AdminProvider/AdminProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import useIsMounted from "ismounted";
import useAdminParams from "hooks/useAdminParams";

import getPortalDetails from "api/getPortalDetails";

import { Switch, Redirect, generatePath } from "react-router-dom";
import PortalResourceRoute from "./PortalResourceRoute";

import Portal from "layouts/Portal/Portal";

import * as routes from ".";
import PortalRoute from "routes/PortalRoute";

const PortalSummary = lazy(() => import("./../pages/PortalSummary/PortalSummary"));
const PortalUsers = lazy(() => import("./../pages/PortalUsers/PortalUsers"));
const PortalLicences = lazy(() => import("./../pages/PortalLicences/PortalLicences"));
const NewPortalLicences = lazy(() => import("pages/PortalLicences/NewPortalLicences"));

const CustomisableItems = lazy(() => import("../pages/CustomisableItems/CustomisableItems"));
const PortalReport = lazy(() => import("../pages/PortalReport/PortalReport"));
const PCATReport = lazy(() => import("../pages/PCATReport/PCATReport"));
const PortalSettings = lazy(() => import("../pages/PortalSettings/PortalSettings"));

const PortalRouter = () => {
  const { ilx58, ilx72, ilx73 } = useFlags();
  const { setPortal } = useAdmin();
  const _mounted = useIsMounted();

  const { portalId } = useAdminParams();

  const setPortalCallback = useCallback(
    (portal) => {
      setPortal(portal);
    },
    [setPortal]
  );

  useEffect(() => {
    if (_mounted.current) {
      if (portalId) {
        getPortalDetails({ portalId }).then(({ data }) => {
          setPortalCallback(data);
        });
      } else {
        setPortalCallback(null);
      }
    }
  }, [_mounted, portalId]);

  return (
    <Switch>
      <PortalRoute exact path={"/"}>
        <Redirect to={routes.PORTAL} />
      </PortalRoute>

      <PortalRoute exact path={routes.PORTAL_ID}>
        <Portal>
          <PortalSummary />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_CONFIGURE}>
        <Redirect to={generatePath(routes.PORTAL_CUSTOMISEABLEITEMS, { portalId })} />
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_CUSTOMISEABLEITEMS}>
        <Portal>
          <CustomisableItems />
        </Portal>
      </PortalRoute>

      {ilx58 && (
        <PortalRoute exact path={routes.PORTAL_PCAT_REPORT}>
          <Portal>
            <PCATReport />
          </Portal>
        </PortalRoute>
      )}

      <PortalRoute exact path={routes.PORTAL_COURSE_REPORT}>
        <Portal>
          <PortalReport />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_USERS}>
        <Portal>
          <PortalUsers />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_USERS_ENROL}>
        <Portal>
          <PortalUsers />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_ADD_USER}>
        <Portal>
          <PortalUsers />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_IMPORT_USERS}>
        <Portal>
          <PortalUsers />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin exact path={routes.PORTAL_USER}>
        <Portal>
          <PortalUsers />
        </Portal>
      </PortalRoute>

      <PortalRoute isPortalAdmin path={routes.PORTAL_RESOURCES}>
        <Portal>
          <PortalResourceRoute />
        </Portal>
      </PortalRoute>

      <PortalRoute isGlobalAdmin exact path={routes.PORTAL_LICENCES}>
        <Portal>{ilx73 ? <NewPortalLicences /> : <PortalLicences />}</Portal>
      </PortalRoute>

      <PortalRoute isGlobalAdmin exact path={routes.PORTAL_SETTINGS}>
        <Portal>
          <PortalSettings />
        </Portal>
      </PortalRoute>
    </Switch>
  );
};

export default PortalRouter;
